import { Component, ElementRef, HostBinding, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import UserUtil from 'app/core/utilities/user-util';
import { AppState, Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { cleanTracks } from 'app/utils/media/stream';
import RingColor from 'entity/RingColor';
import User from 'entity/User';

import { StageSizeType } from '../../performer-participant/performer-participant.types';
import { DeviceService } from '../../services/device/device.service';
import { EmoteService, UserEmote } from '../../services/emote/emote.service';
import { SceneService } from '../../services/scene.service';
import { GroupShareScreenParticipantService } from '../../services/share-screen-group/group-share-screen-participant.service';
import { SideSectionService } from '../../services/side-section.service';
import { VideoRoomService } from '../../services/video-room.service';
import { Player } from '../../social-pixi-canvas/player';
import { PopOutState, socialAppActions, socialAppSelectors } from '../../state/app';
import { BrowserService } from '../../services/browser.service';
const UNDEFINED_STREAM = { id: uuidv4(), stream: undefined };

@Component({
  selector: 'app-presentation-group-share',
  templateUrl: './presentation-group-share.component.html',
  styleUrls: ['./presentation-group-share.component.scss'],
  standalone: false,
})
export class PresentationGroupShareComponent extends Loggable implements OnInit, OnDestroy {
  @Input() @HostBinding('class.drawer-open') public isGalleryDrawerOpen = false;
  @Input() @HostBinding('class.captions-open') public isCaptionsOpen = false;
  @Input() @HostBinding('class.screenshare') public isGroupSharing = false;
  @HostBinding('class.presenters-drawer-is-open') protected isPresentersDrawerOpen = true;

  public user?: User;
  public screenStream?: MediaStream;
  private clickedOnBadge = false;
  public userIsInteracting = false;
  protected isVideoPlaying = false;
  protected isUserSharingScreen = false;

  public set viewSize(value: StageSizeType) {
    this._viewSize = value;
    this.groupShareScreenParticipantService.viewSize = value;
  }

  public get viewSize(): StageSizeType {
    return this._viewSize;
  }

  private _viewSize = StageSizeType.MEDIUM;
  private movementListener?: (this: Window, ev: MouseEvent) => unknown;
  private movementListenerBlocked = false;
  private userIsInteractingTimeout?: Timeout;

  public videoMuted = false;
  public audioMuted = false;
  public presenterEmote?: UserEmote;
  public presentersPlayer?: Player;

  protected isPopout: PopOutState = { mode: 'PRESENTER', open: false };
  protected popOutVideo = false;
  protected fullScreenVideo = true;

  protected ringColors: RingColor[] = [];

  protected isFullScreenMobile = false;
  protected isMobile = false;

  private destroyed$ = new ReplaySubject<boolean>(1);

  @ViewChild('videoElement', { static: true }) private videoElement?: ElementRef<HTMLVideoElement>;
  @ViewChild('videoWrapper', { static: true }) private videoWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('presenter', { static: true }) private presenter?: ElementRef<HTMLVideoElement>;

  public constructor(
    public sideSectionService: SideSectionService,
    private groupShareScreenParticipantService: GroupShareScreenParticipantService,
    private store: Store<AppState>,
    private deviceService: DeviceService,
    private videoRoomService: VideoRoomService,
    private sceneService: SceneService,
    private emoteService: EmoteService,
    private browserService: BrowserService,
  ) {
    super();
    this.setDisplayName('GroupShareScreenParticipantComponent');

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));

    this.store
      .select(socialAppSelectors.isPopOut)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPopOut) => (this.isPopout = isPopOut));

    this.store
      .select(socialAppSelectors.getVisualIndicatorInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visualIndicatorInfo) => {
        if (visualIndicatorInfo.colors) {
          this.ringColors = visualIndicatorInfo.colors;
        }
      });
  }

  public handleMediaStreamChange({ id, stream }: { stream?: MediaStream; id: string }, isShare = true): void {
    const target = isShare ? this.videoElement : this.presenter;
    assertDefined(target);
    const { nativeElement: videoElement } = target;

    if (videoElement.getAttribute('data-stream-id') === id) {
      this.trace('handleMediaStreamChange: skipping video stream update, stream already set as source');
      return;
    }
    videoElement.srcObject = stream ?? null;
    videoElement.setAttribute('data-stream-id', id);

    if (!videoElement.oncanplay) {
      videoElement.oncanplay = () => {
        videoElement.oncanplay = null;
        videoElement
          .play()
          .then(() => {
            this.isVideoPlaying = true;
          })
          .catch((e) => {
            this.isVideoPlaying = false;
            this.error('handleMediaStreamChange: Failed to play video', e);
          });
      };
    }
  }

  public get stageSizeType(): typeof StageSizeType {
    return StageSizeType;
  }

  public ngOnInit(): void {
    // Listen for share media streams
    this.groupShareScreenParticipantService.mediaStream$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ id, stream }) => {
        this.handleMediaStreamChange({ id, stream });
      });
    // Listen for presenter media streams
    this.groupShareScreenParticipantService.groupShareId$
      .pipe(
        switchMap((groupShareId) => {
          this.getPresentersPlayer(groupShareId);
          this.handlePresenterMuted(groupShareId);
          this.handlePresentersEmote(groupShareId);
          // if user is presenter, use camera stream
          if (groupShareId === this.user?.userId) {
            this.isUserSharingScreen = true;
            return this.deviceService.cameraStream$.pipe(
              map((stream) => (stream ? { stream, id: stream.id } : UNDEFINED_STREAM)),
            );
            // else use video room stream
          } else {
            this.isUserSharingScreen = false;
            return this.videoRoomService.participantVideoStream$.pipe(
              map((streams) => streams[groupShareId] ?? UNDEFINED_STREAM),
            );
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ id, stream }) => this.handleMediaStreamChange({ id, stream }, false));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pop_out_video)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((popOutVideo) => (this.popOutVideo = popOutVideo));
    this.declareListenerMethod();
    this.initListeners();
    this.isMobile = this.browserService.isMobile();
  }

  private declareListenerMethod(): void {
    this.movementListener = () => {
      // Blocked while animation
      if (this.movementListenerBlocked) {
        return;
      }

      this.userIsInteracting = true;
      if (this.userIsInteractingTimeout) {
        clearTimeout(this.userIsInteractingTimeout);
      }
      this.userIsInteractingTimeout = setTimeout(() => {
        this.userIsInteracting = false;

        // Block while animation
        this.movementListenerBlocked = true;
        setTimeout(() => (this.movementListenerBlocked = false), 500);
      }, 5000);
    };
  }

  private initListeners(): void {
    if (window && this.movementListener) {
      window.addEventListener('click', this.movementListener, false);
      window.addEventListener('mousemove', this.movementListener, false);
    }
    document.addEventListener('fullscreenchange', this.onFullScreenChange.bind(this));
  }

  @HostBinding('class.is-fullscreen')
  public get isFullScreen(): boolean {
    return this.viewSize === StageSizeType.FULL_SCREEN;
  }

  public toggleFullScreen(force?: StageSizeType): void {
    this.viewSize = force ? force : this.isFullScreen ? StageSizeType.MEDIUM : StageSizeType.FULL_SCREEN;
    this.store.dispatch(socialAppActions.SET_PRESENTATION_STAGE_SIZE({ stageSize: this.viewSize }));
  }

  public toggleVideoDrawer($event: Event): void {
    $event.stopPropagation();
    // don't double toggle when clicking on the button inside the drawer
    if (this.clickedOnBadge) {
      this.clickedOnBadge = false;
      return;
    }
    this.isPresentersDrawerOpen = !this.isPresentersDrawerOpen;
  }

  protected getPresentersPlayer(groupShareId: string) {
    this.presentersPlayer = this.sceneService.sceneManager?.getParticipant(groupShareId);
  }

  private handlePresenterMuted(groupShareId: string) {
    this.store
      .select(socialAppSelectors.getParticipantMediaState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((participant) => {
        if (!participant[groupShareId]) {
          return;
        }
        this.videoMuted = participant[groupShareId].videoMuted;
        this.audioMuted = participant[groupShareId].audioMuted;
      });
  }

  private handlePresentersEmote(groupShareId: string) {
    this.emoteService.emoteStream$.pipe(takeUntil(this.destroyed$)).subscribe((emote) => {
      if (emote.userId === groupShareId && emote.timestamp > Date.now() - this.emoteService.emooteLength) {
        void this.updateEmote(emote);
      }
    });
  }

  private async updateEmote(emote: UserEmote) {
    this.presenterEmote = emote;
    await delayFor(this.emoteService.emooteLength);
    this.presenterEmote = undefined;
  }

  protected formatPresentersName(user?: User): string {
    const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
    return user ? UserUtil.trimName(20, name) : '';
  }

  protected openBadge(userId?: string): void {
    this.clickedOnBadge = true;
    this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: userId ? userId : '' }));
  }

  public get isPresenterPopout(): boolean {
    return this.isPopout.mode === 'PRESENTER' && this.isPopout.open;
  }

  public get isScreenPopout(): boolean {
    return this.isPopout.mode === 'SCREENSHARE' && this.isPopout.open;
  }

  public get roomHasRingColors(): boolean {
    return !!this.ringColors.length;
  }

  public handleFullscreenChange() {
    const videoWrapperElement = this.videoWrapper?.nativeElement;
    assertDefined(videoWrapperElement);

    if (this.isFullScreenMobile) {
      if (document.fullscreenElement === videoWrapperElement) {
        document.exitFullscreen().catch((e) => {
          this.error('handleFullScreen: Failed to toggle OFF fullscreen on video', e);
        });

        this.isFullScreenMobile = false;
      }
    } else {
      videoWrapperElement.requestFullscreen().catch((e) => {
        this.error('handleFullScreen: Failed to toggle ON fullscreen on video', e);
      });
      this.isFullScreenMobile = true;
    }
  }

  private onFullScreenChange() {
    if (!document.fullscreenElement) {
      //Event to turn off fullscreen flag if fullscreen disabled by external events
      this.isFullScreenMobile = false;
    }
  }

  public get isFullScreenEnabled() {
    return !!document.fullscreenEnabled && this.isMobile && !!this.videoElement?.nativeElement;
  }

  public async ngOnDestroy(): Promise<void> {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.viewSize = StageSizeType.CLOSED;

    if (this.isFullScreenMobile) {
      this.handleFullscreenChange();
      this.isFullScreenMobile = false;
    }
    document.removeEventListener('fullscreenchange', this.onFullScreenChange.bind(this));

    clearTimeout(this.userIsInteractingTimeout);

    await cleanTracks(this.screenStream, this);
    if (window && this.movementListener) {
      window.removeEventListener('click', this.movementListener, false);
      window.removeEventListener('mousemove', this.movementListener, false);
    }
  }
}
